import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  adminGetAIProviders, adminCreateAIProvider, adminUpdateAIProvider,
  adminDeleteAIProvider, adminTestAIProvider, adminActivateAIProvider,
  getErrorMessage,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Bot, Plus, RefreshCw, CheckCircle2, XCircle, Trash2, Edit2, Sparkles,
  KeyRound, Zap, AlertTriangle,
} from 'lucide-react';

const KIND_OPTIONS = [
  { value: 'claude', label: 'Anthropic Claude', defaultModel: 'claude-sonnet-4-6', defaultEndpoint: '' },
  { value: 'openai', label: 'OpenAI', defaultModel: 'gpt-4o-mini', defaultEndpoint: '' },
  { value: 'gemini', label: 'Google Gemini', defaultModel: 'gemini-1.5-pro', defaultEndpoint: '' },
  { value: 'azure', label: 'Azure OpenAI', defaultModel: 'gpt-4o', defaultEndpoint: '' },
];

// Preset rapidi per provider compatibili (impostano kind/modello/endpoint).
// Z.AI parla protocollo Anthropic → kind=claude. Gli altri sono OpenAI-compatible.
const COMPATIBLE_PRESETS = [
  { label: 'Z.AI GLM-5.1 (★ Anthropic-compatible)', kind: 'claude', model: 'glm-5.1',
    endpoint: 'https://api.z.ai/api/anthropic/v1/messages', name: 'Z.AI GLM-5.1' },
  { label: 'Z.AI GLM-4.7',  kind: 'claude', model: 'glm-4.7',
    endpoint: 'https://api.z.ai/api/anthropic/v1/messages', name: 'Z.AI GLM-4.7' },
  { label: 'GLM (BigModel)', kind: 'openai', model: 'glm-4-plus',
    endpoint: 'https://open.bigmodel.cn/api/paas/v4/chat/completions', name: 'GLM BigModel' },
  { label: 'DeepSeek',       kind: 'openai', model: 'deepseek-chat',
    endpoint: 'https://api.deepseek.com/v1/chat/completions', name: 'DeepSeek' },
  { label: 'Groq (Llama)',   kind: 'openai', model: 'llama-3.3-70b-versatile',
    endpoint: 'https://api.groq.com/openai/v1/chat/completions', name: 'Groq' },
  { label: 'Mistral',        kind: 'openai', model: 'mistral-large-latest',
    endpoint: 'https://api.mistral.ai/v1/chat/completions', name: 'Mistral' },
];

const empty = {
  name: '', kind: 'claude', api_key: '', endpoint: '', model: 'claude-sonnet-4-6',
  max_tokens: 4096, temperature: 0.7, daily_budget_usd: 5, hard_budget_usd: 15,
};

const AdminAIProvidersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetAIProviders();
      setItems(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Solo admin');
        navigate('/');
      } else {
        toast.error('Errore caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    load();
    /* eslint-disable-next-line */
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      kind: item.kind,
      api_key: '',
      endpoint: item.endpoint || '',
      model: item.model,
      max_tokens: item.max_tokens,
      temperature: item.temperature,
      daily_budget_usd: item.daily_budget_usd,
      hard_budget_usd: item.hard_budget_usd,
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.model.trim()) {
      toast.error('Nome e modello richiesti');
      return;
    }
    const cleanKey = (form.api_key || '').trim();
    if (!editing && !cleanKey) {
      toast.error('API key richiesta');
      return;
    }
    if (cleanKey && (/[\s\r\n\t]/.test(cleanKey) || /[^\x20-\x7E]/.test(cleanKey))) {
      toast.error('API key non valida: rimuovi spazi, a-capo o caratteri speciali — incolla solo la stringa key');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.api_key) delete payload.api_key;
        delete payload.kind;
        await adminUpdateAIProvider(editing.id, payload);
        toast.success('Provider aggiornato');
      } else {
        await adminCreateAIProvider(form);
        toast.success('Provider creato');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore salvataggio'));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try {
      await adminDeleteAIProvider(item.id);
      toast.success('Eliminato');
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore eliminazione'));
    }
  };

  const test = async (item) => {
    setTestingId(item.id);
    try {
      const res = await adminTestAIProvider(item.id);
      if (res.data.ok) {
        toast.success(`${item.name}: connessione ok`);
      } else {
        toast.error(`${item.name}: ${res.data.error || 'errore'}`);
      }
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore test'));
    } finally {
      setTestingId(null);
    }
  };

  const activate = async (item) => {
    try {
      await adminActivateAIProvider(item.id);
      toast.success(`${item.name} è ora il provider di default`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore attivazione'));
    }
  };

  const onKindChange = (kind) => {
    const opt = KIND_OPTIONS.find((k) => k.value === kind);
    setForm((f) => ({ ...f, kind, model: opt?.defaultModel || f.model, endpoint: '' }));
  };

  const applyPreset = (p) => {
    setForm((f) => ({
      ...f,
      kind: p.kind,
      model: p.model,
      endpoint: p.endpoint,
      name: f.name || p.name,
    }));
    toast.info(`${p.label}: incolla l'API key nel campo apposito`);
  };

  return (
    <div className="space-y-6 animate-in" data-testid="admin-ai-providers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Bot className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Provider AI
          </h1>
          <p className="text-muted-foreground">
            Configura uno o più LLM. Solo uno è "default attivo" per le chiamate AI.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={openCreate} data-testid="add-ai-provider-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi provider
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
          {[1, 2].map((i) => <div key={i} className="h-44 bg-muted rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-12 pb-12 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.4} />
            <p className="text-lg font-medium mb-1">Nessun provider configurato</p>
            <p className="text-sm text-muted-foreground mb-4">
              Aggiungi Claude / OpenAI / Gemini per abilitare AI Studio
            </p>
            <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Aggiungi il primo</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <Card key={p.id} className={`relative ${p.active ? 'border-primary/40 ring-1 ring-primary/20' : ''}`} data-testid={`ai-provider-${p.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      {p.name}
                      {p.active && <Badge className="bg-primary/15 text-primary border-0 text-[10px]">attivo</Badge>}
                      <Badge variant="outline" className="text-[10px] capitalize">{p.kind}</Badge>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground truncate font-mono">{p.model}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <KeyRound className="w-3.5 h-3.5" />
                  <span className="font-mono text-xs">{p.api_key_masked || '—'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded bg-muted/40 px-2 py-1.5">
                    <span className="text-muted-foreground">Daily cap</span>
                    <p className="font-semibold tabular-nums">${p.daily_budget_usd}</p>
                  </div>
                  <div className="rounded bg-muted/40 px-2 py-1.5">
                    <span className="text-muted-foreground">Hard cap</span>
                    <p className="font-semibold tabular-nums">${p.hard_budget_usd}</p>
                  </div>
                </div>
                <div className="text-xs">
                  {p.last_test_at ? (
                    p.last_test_ok ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-3 h-3" /> ping ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-500" title={p.last_test_error || ''}>
                        <XCircle className="w-3 h-3" /> ping fallito
                      </span>
                    )
                  ) : (
                    <span className="text-muted-foreground">non testato</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => test(p)} disabled={testingId === p.id}>
                    <Zap className={`w-3.5 h-3.5 mr-1 ${testingId === p.id ? 'animate-pulse' : ''}`} />
                    Test
                  </Button>
                  {!p.active && (
                    <Button size="sm" variant="outline" onClick={() => activate(p)}>
                      Attiva
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-5 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Le API key sono cifrate (Fernet) prima del salvataggio in database.
            La master key vive in <code className="text-xs">ENCRYPTION_KEY</code> nel <code className="text-xs">.env</code> del server.
          </p>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica provider AI' : 'Nuovo provider AI'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="es. Claude principale" data-testid="ai-name-input" />
            </div>
            {!editing && (
              <>
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={form.kind} onValueChange={onKindChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {KIND_OPTIONS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Preset OpenAI-compatible <span className="text-muted-foreground font-normal">(impostano kind, modello e endpoint)</span></Label>
                  <div className="flex flex-wrap gap-1.5">
                    {COMPATIBLE_PRESETS.map((p) => (
                      <button
                        type="button"
                        key={p.label}
                        onClick={() => applyPreset(p)}
                        className="text-xs px-2 py-1 rounded border hover:bg-muted transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>API key {editing && <span className="text-xs text-muted-foreground">(lascia vuoto per non cambiare)</span>}</Label>
              <Input type="password" value={form.api_key}
                     onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                     placeholder={editing ? '••••••••' : 'sk-... / xai-... / AIza...'}
                     data-testid="ai-key-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Modello</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                     placeholder="claude-sonnet-4-6" />
            </div>
            <div className="space-y-1.5">
              <Label>Endpoint custom <span className="text-xs text-muted-foreground">(opzionale)</span></Label>
              <Input value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                     placeholder="https://… (Azure / proxy)" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Max tokens</Label>
                <Input type="number" value={form.max_tokens}
                       onChange={(e) => setForm({ ...form, max_tokens: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1.5">
                <Label>Temperature</Label>
                <Input type="number" step="0.1" min="0" max="2" value={form.temperature}
                       onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Daily cap (USD)</Label>
                <Input type="number" step="0.5" value={form.daily_budget_usd}
                       onChange={(e) => setForm({ ...form, daily_budget_usd: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1.5">
                <Label>Hard cap (USD)</Label>
                <Input type="number" step="0.5" value={form.hard_budget_usd}
                       onChange={(e) => setForm({ ...form, hard_budget_usd: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={submitting} data-testid="ai-submit-btn">
              {submitting ? 'Salvo…' : (editing ? 'Salva modifiche' : 'Crea provider')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAIProvidersPage;
